import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bug, BookOpen, ArrowRight } from 'lucide-react';
import { useAmoebaConfig } from '../hooks/useAmoebaConfig';
import { usePendingCounts } from '../hooks/usePendingCounts';
import api from '../services/api';
import { openBugReport } from '../utils/errorCollector';
import { dashboardSections, access } from '../navigation/modules';
import { pendingChips } from '../navigation/pendingChips';
import ModuleCard from '../components/ModuleCard';

// One colour per section, so a card says where it belongs.
const SECTION_COLOR = {
    mine: '#8b5cf6',
    team: '#0891b2',
    people: '#db2777',
    payroll: '#16a34a',
    tools: '#0ea5e9',
    admin: '#64748b',
};

const Dashboard = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const { config, hasAmoeba } = useAmoebaConfig();

    // How many people report to them (at any depth): their team cards depend on it. HR and Admin see everyone anyway.
    const [teamSize, setTeamSize] = useState(0);
    const isHrOrAdmin = access(user).isHrOrAdmin;
    useEffect(() => {
        if (isHrOrAdmin || !hasAmoeba('ATTENDANCE')) return;
        api.get('/attendance/scope').then(r => setTeamSize(r.data.teamSize || 0)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHrOrAdmin, config.enabledAmoebas]);

    const sections = dashboardSections(user, { teamSize }, hasAmoeba);
    const pending = usePendingCounts();
    const chips = pendingChips(pending);

    return (
        <div className="container section">
            <div style={{ maxWidth: '800px', margin: '0 auto 2rem', textAlign: 'center' }}>
                <h1 className="gradient-text" style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>{config.orgName}</h1>
                {user.name && <p style={{ fontSize: '1.1rem', color: 'var(--text-muted)' }}>Welcome back, <strong>{user.name}</strong></p>}
            </div>

            {chips.length > 0 && (
                <section aria-label="Needs your attention" data-testid="section-attention" style={{ maxWidth: '900px', margin: '0 auto 1rem' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', justifyContent: 'center' }}>
                        {chips.map(c => (
                            <Link
                                key={c.key}
                                to={c.to}
                                data-testid={`chip-${c.key}`}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                                    padding: '0.5rem 1rem', borderRadius: '2rem',
                                    background: 'var(--accent-subtle)', color: 'var(--primary)',
                                    fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none',
                                    border: '1px solid var(--border)',
                                }}
                            >
                                {c.label} <ArrowRight size={14} />
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {sections.map(s => (
                <section key={s.key} aria-labelledby={`section-${s.key}`} data-testid={`section-${s.key}`} style={{ marginTop: '2.5rem' }}>
                    <h2 id={`section-${s.key}`} className="module-section-title" style={{ borderLeft: `4px solid ${SECTION_COLOR[s.key]}` }}>
                        {s.title}
                    </h2>
                    <div className="module-grid">
                        {s.items.map(m => <ModuleCard key={m.key} module={m} color={SECTION_COLOR[s.key]} />)}
                    </div>
                </section>
            ))}

            {/* Help and feedback */}
            <div style={{
                marginTop: '3rem',
                borderTop: '1px solid var(--border)',
                paddingTop: '1.5rem',
                display: 'flex',
                justifyContent: 'center',
                gap: '1rem',
                flexWrap: 'wrap',
                marginBottom: '1rem'
            }}>
                <Link to="/help" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: 'var(--primary)', padding: '0.5rem 1rem', fontWeight: 500 }}>
                    <BookOpen size={16} /> Help Center
                </Link>
                <button
                    type="button"
                    onClick={() => openBugReport()}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: '#ef4444',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        padding: '0.5rem 1rem',
                        borderRadius: '0.5rem',
                        background: 'rgba(239, 68, 68, 0.05)',
                    }}
                >
                    <Bug size={16} /> Report a Bug or Suggestion
                </button>
                <Link to="/bugs" style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem', color: 'var(--text-muted)', padding: '0.5rem 1rem' }}>My reports</Link>
            </div>
        </div>
    );
};

export default Dashboard;
